import { thousandsSeparators } from "../../../../config/utils";
import { CLICK_TO_VIEW_DETAILS, sortChartPoints } from "./utils";

export const WEIGHTED_AVERAGE_CHART_KEY = "Weighted_Average_By_Condition";
export const WEIGHTED_AVERAGE_PARAM_KEY = "weighted_average_chart";
export const WEIGHTED_AVERAGE_HEADING = "Weighted Average by Condition";

export const WEIGHTED_AVERAGE_TYPE_OPTIONS = [
    { label: "Building", value: "building" },
    { label: "Site", value: "site" },
    { label: "Region", value: "region" },
    { label: "Project", value: "project" }
];
export const WEIGHTED_AVERAGE_DEFAULT_TYPE = "region";
export const WEIGHTED_AVERAGE_DEFAULT_SORT = { sortBy: "value", sortAsc: true };

export const getWeightedConditionChartParams = (chartParams = {}, scopeParams = {}, { weightedConditionType, sortBy, sortAsc } = {}) => ({
    ...scopeParams,
    weighted_condition_type: weightedConditionType || WEIGHTED_AVERAGE_DEFAULT_TYPE,
    list: chartParams?.list || {},
    filters: chartParams?.filters || null,
    ...(chartParams?.has_image !== null && chartParams?.has_image !== undefined ? { has_image: chartParams.has_image } : {}),
    ...(chartParams?.verified_survey !== null && chartParams?.verified_survey !== undefined ? { verified_survey: chartParams.verified_survey } : {}),
    chart_sort_by: sortBy || WEIGHTED_AVERAGE_DEFAULT_SORT.sortBy,
    chart_sort_order: sortAsc ? "asc" : "desc",
    sfci_chart_sort_by: chartParams?.sfci_chart_sort_by || "value",
    sfci_chart_sort_order: chartParams?.sfci_chart_sort_order || "desc"
});

const ROW_LIST_KEYS = ["weighted_condition_chart", "weighted_average", "chart_data", "data", "results"];

const findRows = payload => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];
    const namedRows = ROW_LIST_KEYS.map(key => payload[key]).find(value => Array.isArray(value));
    if (namedRows) return namedRows;
    return Object.values(payload).find(value => Array.isArray(value) && (!value.length || typeof value[0] === "object")) || [];
};

export const normalizeWeightedConditionRows = payload =>
    findRows(payload).map(row => ({
        // The id scopes the gauge call for the clicked bar, so accept the entity-specific keys too
        id: row?.id ?? row?.entity_id ?? row?.region_id ?? row?.site_id ?? row?.building_id ?? row?.project_id ?? null,
        name: row?.name || "Unknown",
        value: Number(row?.value ?? 0) || 0,
        color: row?.color ?? null,
        crv: row?.crv ?? null,
        total: row?.total ?? null,
        assetCount: row?.asset_count ?? null,
        condition: row?.asset_condition_name ?? null,
        dm: row?.dm ?? null,
        fc: row?.fc ?? null,
        csp: row?.csp ?? null,
        sf: row?.square_foot_area ?? null
    }));

export const getWeightedAverageRows = chartResponse =>
    (chartResponse?.weighted_average || []).map(item => ({
        name: item.name,
        y: parseFloat(item.value) || 0,
        ...(item.color ? { color: item.color } : {}),
        crv: item.crv,
        total: item.total,
        assetCount: item.assetCount,
        condition: item.condition,
        dm: item.dm,
        fc: item.fc,
        csp: item.csp,
        sf: item.sf,
        entityId: item.id
    }));

// Popup payload for a clicked bar — carries the tooltip fields so the gauge popup can list them
export const getWeightedAveragePopupData = point => ({
    type: WEIGHTED_AVERAGE_CHART_KEY,
    heading: WEIGHTED_AVERAGE_HEADING,
    name: point?.name,
    entityId: point?.entityId,
    value: point?.y,
    assetCount: point?.assetCount,
    condition: point?.condition,
    dm: point?.dm,
    fc: point?.fc,
    csp: point?.csp,
    sf: point?.sf,
    crv: point?.crv
});

// Condition names come from the API and are rendered into a Highcharts label string
const escapeLabel = value =>
    `${value ?? ""}`.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const CHART_MARGIN_LEFT = 74;
const AXIS_LABEL_GUTTER = 10;

const MAX_BAR_WIDTH = 73;
const DENSE_BAR_WIDTH = 26;
const DENSE_BAR_THRESHOLD = 2;
// Highcharts decides per bar whether the label fits past its end. Compare the two rendered boxes on
// screen and colour accordingly: white when the label sits on top of the bar, grey when it sits on the
// plot beside it. (color: "contrast" only distinguishes white vs black, so it is not enough here.)
const OUTSIDE_DATA_LABEL_COLOR = "#666666";
const INSIDE_DATA_LABEL_COLOR = "#FFFFFF";

const paintDataLabels = chart => {
    (chart?.series || []).forEach(series => {
        (series.points || []).forEach(point => {
            const label = point?.dataLabel;
            const barRect = point?.graphic?.element?.getBoundingClientRect?.();
            const labelRect = label?.element?.getBoundingClientRect?.();
            if (!label || !barRect || !labelRect || !labelRect.width) return;
            const labelMiddle = labelRect.left + labelRect.width / 2;
            const isInside = labelMiddle < barRect.right;
            label.css({ color: isInside ? INSIDE_DATA_LABEL_COLOR : OUTSIDE_DATA_LABEL_COLOR });
        });
    });
};

export const removeAxisLabelTitles = chart => {
    const labelNodes = chart?.xAxis?.[0]?.labelGroup?.element?.querySelectorAll("title") || [];
    Array.from(labelNodes).forEach(node => node.remove());
};

const getConditionLegendSeries = (rows, legendSort = { sortBy: "name", sortAsc: true }, hiddenConditions = [], onConditionLegendClick) => {
    const bands = new Map();
    rows.forEach(row => {
        if (!row?.condition || !row?.color) return;
        const band = bands.get(row.condition) || { name: row.condition, color: row.color, y: 0 };
        band.y += Number(row?.y) || 0;
        bands.set(row.condition, band);
    });
    return sortChartPoints([...bands.values()], legendSort?.sortBy, legendSort?.sortAsc).map(({ name: condition, color }) => ({
        name: condition,
        type: "scatter",
        data: [],
        color,
        marker: { symbol: "circle", radius: 5 },
        showInLegend: true,
        enableMouseTracking: false,
        visible: !hiddenConditions.includes(condition),
        events: {
            legendItemClick: function () {
                onConditionLegendClick?.(condition);
                return false;
            }
        }
    }));
};

export const buildWeightedAverageOptions = (
    allRows,
    {
        onAxisLabelHover,
        onAxisLabelOut,
        onPointClick,
        maxVisibleBars = 5,
        showConditionLegend = false,
        legendSort,
        hiddenConditions = [],
        onConditionLegendClick
    } = {}
) => {
    const rows = hiddenConditions.length ? allRows.filter(row => !hiddenConditions.includes(row?.condition)) : allRows;
    const isScroll = rows.length > maxVisibleBars;
    const visibleBars = Math.min(rows.length, maxVisibleBars);
    const maxPointWidth = visibleBars > DENSE_BAR_THRESHOLD ? DENSE_BAR_WIDTH : MAX_BAR_WIDTH;
    const legendSeries = showConditionLegend ? getConditionLegendSeries(allRows, legendSort, hiddenConditions, onConditionLegendClick) : [];
    return {
        exporting: { enabled: false },
        credits: { enabled: false },
        chart: {
            type: "bar",
            style: { fontFamily: "Poppins, sans-serif" },
            marginLeft: CHART_MARGIN_LEFT,
            animation: false,
            backgroundColor: "#FFFFFF",
            events: {
                render: function () {
                    removeAxisLabelTitles(this);
                    paintDataLabels(this);
                }
            }
        },
        title: { text: "" },
        subtitle: { text: "" },
        xAxis: {
            id: "x-axis-1",
            categories: rows.map(row => row.name),
            title: { text: null },
            scrollbar: { enabled: isScroll },
            labels: {
                enabled: true,
                style: {
                    color: "#000000",
                    cursor: "default",
                    width: `${CHART_MARGIN_LEFT - AXIS_LABEL_GUTTER}px`,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis"
                },
                events: {
                    mouseover: function (event) {
                        onAxisLabelHover?.(this.axis.categories?.[this.pos], event?.currentTarget || event?.target);
                    },
                    mouseout: function () {
                        onAxisLabelOut?.();
                    }
                }
            },
            min: 0,
            max: Math.max(isScroll ? maxVisibleBars - 1 : rows.length - 1, 0)
        },
        yAxis: {
            min: 0,
            title: { text: "", align: "high" },
            labels: { overflow: "justify" }
        },
        tooltip: {
            useHTML: true,
            backgroundColor: "transparent",
            borderWidth: 0,
            shadow: false,
            formatter: function () {
                const point = this.point || {};
                const textSpan = text =>
                    text !== null && text !== "" && text !== undefined
                        ? `<span style="font-weight: 550">${text}</span>`
                        : `<span style="font-weight: 550">NA</span>`;
                const currency = value => (value != null ? `$ ${(value / 1000000).toFixed(2)}M` : null);
                const area = value => (value != null && value !== "" ? thousandsSeparators(Math.round(value)) : null);
                return `
                    <div style="background: #e6e6e6; padding: 8px 10px; position: relative; z-index: 999;">
                    <div class='tooltip-fca-line'>${textSpan(point.name)}</div>
                    <div class='tooltip-fca-line' style="margin-right: 4px;">
                        <span style="white-space: nowrap; margin-right: 10px;">Weighted Average: ${textSpan(this.y)}</span>
                        <span style="white-space: nowrap; margin-right: 10px;">Assets: ${textSpan(area(point.assetCount))}</span>
                        <span style="white-space: nowrap; margin-right: 10px;">Condition: ${textSpan(point.condition)}</span>
                    </div>
                    <div class='tooltip-fca-line' style="margin-right: 4px;">
                        <span style="white-space: nowrap; margin-right: 10px;">DM: ${textSpan(currency(point.dm))}</span>
                        <span style="white-space: nowrap; margin-right: 10px;">FC: ${textSpan(currency(point.fc))}</span>
                        <span style="white-space: nowrap; margin-right: 10px;">CSP: ${textSpan(currency(point.csp))}</span>
                        <span style="white-space: nowrap; margin-right: 10px;">SF: ${textSpan(area(point.sf))}</span>
                    </div>
                    <div class='tooltip-fca-line'>${CLICK_TO_VIEW_DETAILS}</div>
                    </div>`;
            },
            positioner: function (labelWidth, labelHeight, point) {
                const plotWidth = this?.chart?.plotWidth;
                const pointHeight = point?.shapeArgs ? point?.shapeArgs?.height : 0;
                let tooltipX = this?.chart?.plotLeft - 20;
                let tooltipY = point?.plotY + this?.chart?.plotTop - labelHeight - 10;
                if (tooltipX + labelWidth > this?.chart?.plotLeft + plotWidth) {
                    tooltipX = this?.chart?.plotLeft - 70;
                }
                if (tooltipY < this?.chart?.plotTop) {
                    tooltipY = point?.plotY + this?.chart?.plotTop + pointHeight + 10;
                }
                return { x: tooltipX, y: tooltipY };
            }
        },
        plotOptions: {
            bar: {
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        // The bar's condition band is more useful on the bar than the word "Average"
                        const conditionName = this.point?.condition;
                        if (!conditionName) return `<span style="font-weight: 900;">${this.y}</span>`;
                        return `<span style="font-weight: 600;">${escapeLabel(conditionName)}</span>: <span style="font-weight: 900;">${this.y}</span>`;
                    },
                    // Highcharts' "contrast" colour: dark while the label sits outside the bar on the
                    // white plot, white once it has to sit inside the bar — same as FCI Benchmarking.
                    // Default crop/overflow ("justify") is what moves a label inside when it no longer
                    // fits past the bar's end; allowing overflow would keep it outside and dark.
                    style: { fontSize: "12px", color: "contrast" }
                },
                groupPadding: 0.08,
                pointPadding: 0.04,
                maxPointWidth
            },
            series: {
                animation: { duration: 1000, easing: "easeOutBounce" },
                dataLabels: {
                    enabled: true,
                    style: { color: "contrast" }
                },
                cursor: onPointClick ? "pointer" : undefined,
                // A clicked/hovered bar must keep its own colour: the select state would grey it and
                // the inactive state would fade the rest, both of which restyle the in-bar labels
                states: {
                    select: { color: null, borderColor: null },
                    inactive: { opacity: 1 }
                },
                point: {
                    events: {
                        // A clicked bar opens the condition gauge popup (same gauge as FCI Benchmarking)
                        click: function (event) {
                            onPointClick?.(this, event);
                        }
                    }
                }
            }
        },
        legend: {
            enabled: !!legendSeries.length,
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            className: "custom-chart-legend"
        },
        series: [{ name: WEIGHTED_AVERAGE_HEADING, data: rows, showInLegend: false }, ...legendSeries]
    };
};
